import { WritingChunks } from '@/types/frontend-types';

export const LINE_DROP_TEXT = ' ';
export const BULLSHIT_CODING_VISIBLE_LINE_LIMIT = 10;
export const CYCLE_END_EMPTY_LINE_COUNT = 15;
export const TYPEWRITER_START_DELAY_MS = 10;

export enum CbullshitLineTypingMode {
  Instant = 'instant',
  Word = 'word',
  Letter = 'letter',
}

export interface ColoredSegment {
  text: string;
  color: string | null;
}

export interface DisplayLine {
  plain: string;
  segments: ColoredSegment[];
}

export interface LineTypingPlan {
  mode: CbullshitLineTypingMode;
  pauseMs: number;
}

export interface ActiveLineTypingInput {
  line: DisplayLine;
  lineText: string;
  lineIndex: number;
  pauseMs: number;
}

const LINE_TYPING_MODE_BY_WRITING_CHUNKS: Record<WritingChunks, CbullshitLineTypingMode> = {
  [WritingChunks.Line]: CbullshitLineTypingMode.Instant,
  [WritingChunks.Word]: CbullshitLineTypingMode.Word,
  [WritingChunks.Letter]: CbullshitLineTypingMode.Letter,
};

const TYPING_CURSOR_BY_WRITING_CHUNKS: Record<WritingChunks, string> = {
  [WritingChunks.Line]: '',
  [WritingChunks.Word]: '',
  [WritingChunks.Letter]: '_',
};

interface LineTypingPlanInput {
  lineText: string;
  lineIndex: number;
  lineCount: number;
  writingChunks: WritingChunks;
  trailingLineDropCount: number;
  trailingLineDropPauseMs: number;
  pauseBetweenLinesMs: number;
}

export function appendVisibleLine(input: {
  lines: DisplayLine[];
  line: DisplayLine;
}): DisplayLine[] {
  const nextLines = [...input.lines, input.line];
  return nextLines.length > BULLSHIT_CODING_VISIBLE_LINE_LIMIT
    ? nextLines.slice(1)
    : nextLines;
}

export function createLineDropDisplayLine(): DisplayLine {
  return {
    plain: LINE_DROP_TEXT,
    segments: [{ text: LINE_DROP_TEXT, color: null }],
  };
}

export function getNextLineIndex(input: {
  lineIndex: number;
  lineCount: number;
}): number {
  return (input.lineIndex + 1) % input.lineCount;
}

export function isCycleComplete(input: { nextLineIndex: number }): boolean {
  return input.nextLineIndex === 0;
}

function isTrailingLineDrop(input: LineTypingPlanInput): boolean {
  return (
    input.trailingLineDropCount > 0 &&
    input.lineText === LINE_DROP_TEXT &&
    input.lineIndex >= input.lineCount - input.trailingLineDropCount
  );
}

function getLineTypingMode(input: {
  isTrailingLineDrop: boolean;
  writingChunks: WritingChunks;
}): CbullshitLineTypingMode {
  if (input.isTrailingLineDrop) {
    return CbullshitLineTypingMode.Instant;
  }

  return LINE_TYPING_MODE_BY_WRITING_CHUNKS[input.writingChunks];
}

function getLinePauseMs(input: {
  isTrailingLineDrop: boolean;
  trailingLineDropPauseMs: number;
  pauseBetweenLinesMs: number;
}): number {
  return input.isTrailingLineDrop
    ? input.trailingLineDropPauseMs
    : input.pauseBetweenLinesMs;
}

export function getLineTypingPlan(input: LineTypingPlanInput): LineTypingPlan {
  const trailingLineDrop = isTrailingLineDrop(input);
  return {
    mode: getLineTypingMode({
      isTrailingLineDrop: trailingLineDrop,
      writingChunks: input.writingChunks,
    }),
    pauseMs: getLinePauseMs({
      isTrailingLineDrop: trailingLineDrop,
      trailingLineDropPauseMs: input.trailingLineDropPauseMs,
      pauseBetweenLinesMs: input.pauseBetweenLinesMs,
    }),
  };
}

export function getTypingCursor(input: { writingChunks: WritingChunks }): string {
  return TYPING_CURSOR_BY_WRITING_CHUNKS[input.writingChunks];
}

export function getCenteredBlockWidthCh(input: {
  centerBlock: boolean;
  lines: DisplayLine[];
}): number | null {
  if (!input.centerBlock || input.lines.length === 0) {
    return null;
  }

  return Math.max(...input.lines.map((line) => line.plain.length));
}

export function getWordCount(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

export function getWordTypingFrames(lineText: string): string[] {
  const words = lineText.match(/\S+\s*/g) ?? [lineText];
  const typingFrames: string[] = [];
  words.reduce((currentText, word) => {
    const nextText = `${currentText}${word}`;
    typingFrames.push(nextText);
    return nextText;
  }, '');
  return typingFrames;
}
